import strings from 'common/Translation/Translate';
import type { KeyboardEvent } from 'react';
import { Validators } from '../../utils/validators';

/** Field names used by Organization (and mapped from Venue Config). */
export type ConfigPaymentScheduleValues = {
  minConfirmPaymentPercentage?: number | string | null;
  paymentSlotOnePercentage?: number | string | null;
  paymentSlotTwoPercentage?: number | string | null;
  paymentSlotThreePercentage?: number | string | null;
  paymentSlotOneBeforeDays?: number | string | null;
  paymentSlotTwoBeforeDays?: number | string | null;
  fullPaymentBeforeDays?: number | string | null;
};

export type ConfigPaymentScheduleRow =
  | {
      id: string;
      label: string;
      percentField: keyof ConfigPaymentScheduleValues;
      dueType: 'on_confirmation';
    }
  | {
      id: string;
      label: string;
      percentField: keyof ConfigPaymentScheduleValues;
      daysField: keyof ConfigPaymentScheduleValues;
      dueType: 'days_before';
    };

export const getConfigPaymentScheduleRows = (): ConfigPaymentScheduleRow[] => [
  {
    id: 'confirmation',
    label: strings.payment_schedule_confirmation,
    percentField: 'minConfirmPaymentPercentage',
    dueType: 'on_confirmation',
  },
  {
    id: 'slot1',
    label: strings.payment_schedule_first_instalment,
    percentField: 'paymentSlotOnePercentage',
    daysField: 'paymentSlotOneBeforeDays',
    dueType: 'days_before',
  },
  {
    id: 'slot2',
    label: strings.payment_schedule_second_instalment,
    percentField: 'paymentSlotTwoPercentage',
    daysField: 'paymentSlotTwoBeforeDays',
    dueType: 'days_before',
  },
  {
    id: 'slot3',
    label: strings.payment_schedule_final_payment,
    percentField: 'paymentSlotThreePercentage',
    daysField: 'fullPaymentBeforeDays',
    dueType: 'days_before',
  },
];

export const CONFIG_PAYMENT_SCHEDULE_FIELD_KEYS: (keyof ConfigPaymentScheduleValues)[] = [
  'minConfirmPaymentPercentage',
  'paymentSlotOnePercentage',
  'paymentSlotTwoPercentage',
  'paymentSlotThreePercentage',
  'paymentSlotOneBeforeDays',
  'paymentSlotTwoBeforeDays',
  'fullPaymentBeforeDays',
];

export const pickConfigPaymentScheduleValues = (source: Record<string, unknown>): ConfigPaymentScheduleValues =>
  CONFIG_PAYMENT_SCHEDULE_FIELD_KEYS.reduce((acc, key) => {
    acc[key] = source[key as string] as ConfigPaymentScheduleValues[keyof ConfigPaymentScheduleValues];
    return acc;
  }, {} as ConfigPaymentScheduleValues);

/** Venue Config API uses `minConfirmationPercentage` instead of `minConfirmPaymentPercentage`. */
export const venuePaymentToScheduleValues = (source: Record<string, unknown>): ConfigPaymentScheduleValues => ({
  minConfirmPaymentPercentage: (source.minConfirmationPercentage ?? source.minConfirmPaymentPercentage) as
    | number
    | string
    | null
    | undefined,
  paymentSlotOnePercentage: source.paymentSlotOnePercentage as number | string | null | undefined,
  paymentSlotTwoPercentage: source.paymentSlotTwoPercentage as number | string | null | undefined,
  paymentSlotThreePercentage: source.paymentSlotThreePercentage as number | string | null | undefined,
  paymentSlotOneBeforeDays: source.paymentSlotOneBeforeDays as number | string | null | undefined,
  paymentSlotTwoBeforeDays: source.paymentSlotTwoBeforeDays as number | string | null | undefined,
  fullPaymentBeforeDays: source.fullPaymentBeforeDays as number | string | null | undefined,
});

const emptyToNull = (value: unknown) => (value === '' || value == null ? null : value);

export const scheduleValuesToVenuePayment = (values: ConfigPaymentScheduleValues) => ({
  minConfirmationPercentage: emptyToNull(values.minConfirmPaymentPercentage),
  paymentSlotOnePercentage: emptyToNull(values.paymentSlotOnePercentage),
  paymentSlotTwoPercentage: emptyToNull(values.paymentSlotTwoPercentage),
  paymentSlotThreePercentage: emptyToNull(values.paymentSlotThreePercentage),
  paymentSlotOneBeforeDays: emptyToNull(values.paymentSlotOneBeforeDays),
  paymentSlotTwoBeforeDays: emptyToNull(values.paymentSlotTwoBeforeDays),
  fullPaymentBeforeDays: emptyToNull(values.fullPaymentBeforeDays),
});
const validatorMessage = (message: string | (string | number)[]): string =>
  Array.isArray(message) ? message.map(String).join('') : message;

export const sanitizeNonNegativeIntInput = (raw: string, max: number): string => {
  if (raw === '') return '';
  const digitsOnly = raw.replace(/\D/g, '');
  if (digitsOnly === '') return '';
  return String(Math.min(parseInt(digitsOnly, 10), max));
};

export const parseDaysValue = (value: unknown): number | null => {
  if (value === '' || value == null) return null;
  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Days-before should decrease over time: 1st >= 2nd >= final. */
export const getDaysOrderWarnings = (
  values: ConfigPaymentScheduleValues,
): Partial<Record<'paymentSlotTwoBeforeDays' | 'fullPaymentBeforeDays', boolean>> => {
  const first = parseDaysValue(values.paymentSlotOneBeforeDays);
  const second = parseDaysValue(values.paymentSlotTwoBeforeDays);
  const final = parseDaysValue(values.fullPaymentBeforeDays);

  return {
    paymentSlotTwoBeforeDays: first != null && second != null && second > first,
    fullPaymentBeforeDays:
      final != null &&
      ((first != null && final > first) || (second != null && final > second)),
  };
};

export const formatPaymentDueLabel = (days: unknown): string => {
  if (days === '' || days == null) return strings.payment_schedule_days_before;
  const parsed = parseInt(String(days), 10);
  if (!Number.isFinite(parsed)) return strings.payment_schedule_days_before;
  if (parsed === 0) return strings.payment_schedule_on_event_day;
  return strings.payment_schedule_days_before;
};

export const blockNegativeNumberKeys = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
    event.preventDefault();
  }
};

const validatePercent = (
  value: unknown,
  field: string,
  errors: Record<string, string>,
  required: boolean,
) => {
  if (value === '' || value == null) {
    if (required) {
      const mandatory = Validators.validateMandatoryField(String(value ?? ''), '');
      if (mandatory && mandatory.error) {
        errors[field] = validatorMessage(mandatory.message);
      }
    }
    return;
  }
  const result = Validators.validateInt(String(value), '', 0, 100);
  if (result && result.error) {
    errors[field] = validatorMessage(result.message);
  }
};

const validateRequiredDays = (value: unknown, field: string, errors: Record<string, string>, maxDays = 100) => {
  const mandatory = Validators.validateMandatoryField(String(value ?? ''), '');
  if (mandatory && mandatory.error) {
    errors[field] = validatorMessage(mandatory.message);
    return;
  }
  const result = Validators.validateInt(String(value), '', 0, maxDays);
  if (result && result.error) {
    errors[field] = validatorMessage(result.message);
  }
};

export const validateConfigPaymentSchedule = (
  values: ConfigPaymentScheduleValues,
  options?: { requirePercents?: boolean },
): { valid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};
  const requirePercents = options?.requirePercents ?? false;

  validatePercent(values.minConfirmPaymentPercentage, 'minConfirmPaymentPercentage', errors, requirePercents);
  validatePercent(values.paymentSlotOnePercentage, 'paymentSlotOnePercentage', errors, requirePercents);
  validatePercent(values.paymentSlotTwoPercentage, 'paymentSlotTwoPercentage', errors, requirePercents);
  validatePercent(values.paymentSlotThreePercentage, 'paymentSlotThreePercentage', errors, requirePercents);

  validateRequiredDays(values.paymentSlotOneBeforeDays, 'paymentSlotOneBeforeDays', errors);
  validateRequiredDays(values.paymentSlotTwoBeforeDays, 'paymentSlotTwoBeforeDays', errors);
  validateRequiredDays(values.fullPaymentBeforeDays, 'fullPaymentBeforeDays', errors);

  return { valid: Object.keys(errors).length === 0, errors };
};
